/**
 * ScoutModal.tsx — T-1 Universal Scout-the-Tile minigame.
 *
 * Shown on terrain tiles when the Scout NPC (builder-e1.png, x=0.55) is interacted with.
 * Gated on boardAnimationActive === false (R11.2 invariant).
 *
 * VISIBLE WHEN: pendingScout !== null AND boardAnimationActive === false
 * TEST PATH: land on terrain tile → interact with Scout NPC (E-key, x≈0.55)
 *            → ScoutModal renders.
 *
 * Flow (R19-GB-4 two-step):
 *   1. Modal mounts with terrain name + entry fee (era-scaled).
 *   2. Player clicks "Pay & Roll": payScoutFee() fires immediately (fee debited,
 *      d6 rolled, result stored in pendingScout.roll/outcome). No effects yet.
 *   3. 1.5s animated reveal using the real roll from pendingScout.
 *   4. Outcome text + 1-line SDG educational fact.
 *   5. Player clicks "Continue": applyScoutOutcome() fires (resource deltas applied),
 *      then modal closes. Scout NPC marked completed for this landing.
 *   6. "Cancel" closes without paying (only valid before Pay & Roll).
 */

import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useI18n } from "../../i18n/useI18n.js";
import { scoutCostForEra, useGameStore } from "../../store/game-store.js";
import styles from "./ScoutModal.module.css";

// ---------------------------------------------------------------------------
// Scout table row types (mirrors game-store helpers)
// ---------------------------------------------------------------------------

type ScoutOutcomeEffect = {
  tokens?: number;
  pollution?: number;
  opponentPenalty?: number;
  queueDiscount?: string;
  tourismBonus?: number;
  policyDiscount?: number;
  text: string;
  sdgFact: string;
};
type ScoutOutcomeRow = "nothing" | ScoutOutcomeEffect;

// ---------------------------------------------------------------------------
// Resource delta display helpers
// ---------------------------------------------------------------------------

interface DeltaEntry {
  label: string;
  value: number;
  /** When true, positive = bad (pollution) so colour polarity flips */
  negativeIsGood?: boolean;
}

/** Build a list of delta entries from an outcome effect for display. */
function buildDeltaEntries(outcome: ScoutOutcomeEffect): DeltaEntry[] {
  const entries: DeltaEntry[] = [];
  if (outcome.tokens !== undefined && outcome.tokens !== 0) {
    entries.push({ label: "Tokens", value: outcome.tokens });
  }
  if (outcome.pollution !== undefined && outcome.pollution !== 0) {
    // pollution negative = clean outcome (good)
    entries.push({ label: "Pollution", value: outcome.pollution, negativeIsGood: true });
  }
  if (outcome.tourismBonus !== undefined && outcome.tourismBonus !== 0) {
    entries.push({ label: "Tourism bonus", value: outcome.tourismBonus });
  }
  if (outcome.opponentPenalty !== undefined && outcome.opponentPenalty !== 0) {
    entries.push({ label: "Opponent penalty", value: outcome.opponentPenalty });
  }
  if (outcome.policyDiscount !== undefined && outcome.policyDiscount !== 0) {
    entries.push({ label: "Policy discount", value: outcome.policyDiscount });
  }
  return entries;
}

/** Format a single delta entry to a human-readable string. */
function formatDelta(entry: DeltaEntry): string {
  const sign = entry.value > 0 ? "+" : "";
  return `${entry.label}: ${sign}${entry.value}`;
}

/** True when the delta should render in the "good" (green) colour. */
function isDeltaPositive(entry: DeltaEntry): boolean {
  if (entry.negativeIsGood === true) return entry.value <= 0;
  return entry.value >= 0;
}

// ---------------------------------------------------------------------------
// SDG facts for "nothing" outcomes — shown so every roll teaches something.
// ---------------------------------------------------------------------------

const NOTHING_SDG_FACTS = [
  "SDG 15: Over 70% of Earth's ice-free land has been altered by human activity.",
  "SDG 13: Each decade is now warmer than the previous since the 1980s.",
  "SDG 7: About 733 million people still lack access to electricity globally.",
  "SDG 11: By 2030, 60% of the world's population will live in urban areas.",
  "SDG 14: Oceans absorb about 30% of the CO₂ produced by human activities.",
  "SDG 12: If everyone lived like the average American, we'd need 5 Earths.",
] as const;

function pickNothingFact(roll: number): string {
  return NOTHING_SDG_FACTS[roll % NOTHING_SDG_FACTS.length] ?? NOTHING_SDG_FACTS[0];
}

// ---------------------------------------------------------------------------
// Animated die face
// ---------------------------------------------------------------------------

const DIE_FACES = ["⚀", "⚁", "⚂", "⚃", "⚄", "⚅"] as const;

interface DieProps {
  rolling: boolean;
  finalRoll: number | null;
}

function AnimatedDie({ rolling, finalRoll }: DieProps): React.ReactElement {
  const [displayRoll, setDisplayRoll] = useState<number>(1);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!rolling) {
      if (timerRef.current !== null) window.clearInterval(timerRef.current);
      return;
    }
    // Shuffle display every 80ms while rolling
    timerRef.current = window.setInterval(() => {
      setDisplayRoll(Math.floor(Math.random() * 6) + 1);
    }, 80);
    return () => {
      if (timerRef.current !== null) window.clearInterval(timerRef.current);
    };
  }, [rolling]);

  const face = finalRoll !== null ? DIE_FACES[finalRoll - 1] : DIE_FACES[displayRoll - 1];
  return (
    <span
      className={styles.dieFace}
      data-testid="scout-die"
      aria-label={`Die showing ${finalRoll ?? displayRoll}`}
    >
      {face}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Scout modal phases
// ---------------------------------------------------------------------------

type ModalPhase = "offer" | "rolling" | "reveal";

// ---------------------------------------------------------------------------
// Main ScoutModal component
// ---------------------------------------------------------------------------

export function ScoutModal(): React.ReactElement | null {
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  const pendingScout = useGameStore((s) => s.pendingScout);
  const era = useGameStore((s) => s.era);
  const currentSeat = useGameStore((s) => s.currentSeat);
  const seatTokens = useGameStore((s) => s.tokens[s.currentSeat] ?? 0);
  const payScoutFee = useGameStore((s) => s.payScoutFee);
  const applyScoutOutcome = useGameStore((s) => s.applyScoutOutcome);
  const cancelScout = useGameStore((s) => s.cancelScout);

  // R13 Codex-B HIGH 3: latch scout locally so the reveal phase survives
  // pendingScout being cleared by applyScoutOutcome(). Inner controls unmount via
  // onComplete callback (called by Continue / Cancel).
  const [activeScout, setActiveScout] = useState<{ terrain: string; tileIdx: number } | null>(null);

  useEffect(() => {
    if (pendingScout !== null && activeScout === null) {
      setActiveScout({ terrain: pendingScout.terrain, tileIdx: pendingScout.tileIdx });
    }
  }, [pendingScout, activeScout]);

  if (boardAnimationActive) return null;
  if (activeScout === null) return null;

  const cost = scoutCostForEra(era);
  return (
    <ScoutModalInner
      key={`scout-${activeScout.tileIdx}-${currentSeat}`}
      terrain={activeScout.terrain}
      cost={cost}
      seatTokens={seatTokens}
      payScoutFee={payScoutFee}
      applyScoutOutcome={applyScoutOutcome}
      cancelScout={cancelScout}
      onComplete={() => setActiveScout(null)}
    />
  );
}

// ---------------------------------------------------------------------------
// Inner (keyed to reset state on each new offer)
// ---------------------------------------------------------------------------

interface InnerProps {
  terrain: string;
  cost: number;
  seatTokens: number;
  payScoutFee: () => void;
  applyScoutOutcome: () => void;
  cancelScout: () => void;
  onComplete: () => void;
}

function ScoutModalInner({
  terrain,
  cost,
  seatTokens,
  payScoutFee,
  applyScoutOutcome,
  cancelScout,
  onComplete,
}: InnerProps): React.ReactElement {
  const { t } = useI18n();
  const canAfford = seatTokens >= cost;
  const [phase, setPhase] = useState<ModalPhase>("offer");
  // Latched real roll from pendingScout after payScoutFee fires.
  const [latchedRoll, setLatchedRoll] = useState<number | null>(null);
  const [latchedOutcome, setLatchedOutcome] = useState<ScoutOutcomeRow | null>(null);
  const rollTimerRef = useRef<number | null>(null);
  const revealTimerRef = useRef<number | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rollTimerRef.current !== null) window.clearTimeout(rollTimerRef.current);
      if (revealTimerRef.current !== null) window.clearTimeout(revealTimerRef.current);
    };
  }, []);

  const handlePayAndRoll = useCallback(() => {
    if (phase !== "offer") return;
    if (!canAfford) return; // codex MED 4: guard rail
    // R19-GB-4: fee deducted immediately at click time (payScoutFee is sync).
    payScoutFee();
    // Read the real roll from store immediately after the sync set.
    const storeRoll = useGameStore.getState().pendingScout?.roll ?? null;
    const storeOutcome = useGameStore.getState().pendingScout?.outcome ?? null;
    setLatchedRoll(typeof storeRoll === "number" ? storeRoll : null);
    setLatchedOutcome(storeOutcome ?? null);
    // Animate rolling for 1s, then reveal outcome.
    setPhase("rolling");
    rollTimerRef.current = window.setTimeout(() => {
      revealTimerRef.current = window.setTimeout(() => {
        setPhase("reveal");
      }, 500); // brief pause after die settles
    }, 1000);
  }, [phase, canAfford, payScoutFee]);

  // R19-GB-4: Continue applies the outcome deltas, then closes.
  const handleContinue = useCallback(() => {
    applyScoutOutcome();
    onComplete();
  }, [applyScoutOutcome, onComplete]);

  const handleCancel = useCallback(() => {
    cancelScout();
    onComplete();
  }, [cancelScout, onComplete]);

  // Display outcome: use latched real outcome (from store roll), fall back to null.
  const displayOutcome: ScoutOutcomeRow | null = latchedOutcome;

  return (
    <div
      className={styles.overlay}
      // biome-ignore lint/a11y/useSemanticElements: overlay needs z-index stacking context
      role="dialog"
      aria-modal="true"
      aria-label="Scout the Tile"
      data-testid="scout-modal"
    >
      <div className={styles.panel}>
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.terrainBadge} data-testid="scout-terrain-badge">
            {terrain}
          </span>
          <span className={styles.headerTitle}>{t("scout.title")}</span>
        </div>

        {/* Die display */}
        <div className={styles.dieRow}>
          <AnimatedDie
            rolling={phase === "rolling"}
            finalRoll={phase === "reveal" ? latchedRoll : null}
          />
        </div>

        {/* Phase-specific content */}
        {phase === "offer" && (
          <>
            <p className={styles.desc} data-testid="scout-desc">
              Pay <strong>{cost}T</strong> to scout this tile for resources and opportunities.
            </p>
            {/* Codex Phase 2b MED 4 fix: surface insufficient-funds state on
                the offer screen so the player isn't surprised by a no-op. */}
            {!canAfford && (
              <p
                className={styles.desc}
                data-testid="scout-cant-afford"
                style={{ color: "#ea5656", marginTop: 6 }}
              >
                ⚠ Insufficient tokens — you have {seatTokens}T, need {cost}T.
              </p>
            )}
            <div className={styles.footer}>
              <button
                type="button"
                className={styles.payBtn}
                onClick={handlePayAndRoll}
                data-testid="scout-pay-btn"
                disabled={!canAfford}
                title={canAfford ? undefined : `Need ${cost}T (you have ${seatTokens}T)`}
              >
                Pay & Roll ({cost}T)
              </button>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={handleCancel}
                data-testid="scout-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {phase === "rolling" && (
          <p className={styles.desc} aria-live="polite">
            Scouting…
          </p>
        )}

        {phase === "reveal" && (
          <>
            {/* R13 Bug #2: always show outcome + SDG fact, even for "nothing" rolls */}
            {displayOutcome === null || displayOutcome === "nothing" ? (
              <div className={styles.outcomeNothing} data-testid="scout-outcome-nothing">
                <p className={styles.outcomeNothingText}>{t("scout.nothingFound")}</p>
                <p className={styles.sdgFact} data-testid="scout-sdg-fact">
                  {pickNothingFact(latchedRoll ?? 0)}
                </p>
              </div>
            ) : (
              <div className={styles.outcomeCard} data-testid="scout-outcome-card">
                <p className={styles.outcomeText} data-testid="scout-outcome-text">
                  {displayOutcome.text}
                </p>
                {/* Resource deltas — shown above the SDG fact so the player
                    sees the mechanical outcome before the educational content. */}
                {buildDeltaEntries(displayOutcome).length > 0 ? (
                  <div data-testid="scout-outcome-deltas">
                    {buildDeltaEntries(displayOutcome).map((entry) => (
                      <p
                        key={entry.label}
                        className={`${styles.deltaLine}${isDeltaPositive(entry) ? "" : ` ${styles.deltaNegative}`}`}
                        data-testid={`scout-delta-${entry.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {formatDelta(entry)}
                      </p>
                    ))}
                  </div>
                ) : null}
                <p className={styles.sdgFact} data-testid="scout-sdg-fact">
                  {displayOutcome.sdgFact}
                </p>
              </div>
            )}
            <div className={styles.footer}>
              <button
                type="button"
                className={styles.continueBtn}
                onClick={handleContinue}
                data-testid="scout-continue-btn"
              >
                Continue
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
